import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  Button,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  ArrowLeft,
  Save,
  Info,
  Upload,
  Settings,
  FileText,
  Zap,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { PilotApis } from 'apis/pilotAPI';
import { BglOverlayLoader } from 'components/loading';
import { PipelineSection } from '../../constants/pipeline-components-data';
import { PipelineMasterApis } from 'apis/pipelineAPI';
import { Workflow } from 'lucide-react';
import { 
  PipelineProcessComponents,
  getComponentKey,
  createMinimalPayload,
  COMPONENT_KEYS
} from '../../types/apis/pipelineProcessInterfaces';
import { BglSelect, BglOption } from 'components/common/BglSelect';



const ComponentItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
}));

const FileUploadSection = styled(Box)(({ theme }) => ({
  padding: '12px 12px',
  backgroundColor: theme.palette.secondary.main,
  borderLeft: `1px dashed ${theme.palette.secondary.dark}`,
  marginLeft: "16px",
  alignItems: 'left',
  textAlign: 'left'
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 500,
  minHeight: 40,
  padding: '8px 16px',
}));

const getSectionIcon = (iconName: string, theme: any) => {
  const iconColor = theme.palette.text.secondary;
  switch (iconName) {
    case 'processing':
      return <Settings size={20} color={iconColor} />;
    case 'content':
      return <FileText size={20} color={iconColor} />;
    case 'advanced':
      return <Zap size={20} color={iconColor} />;
    default:
      return <Settings size={20} color={iconColor} />;
  }
};

// Note: Component identification is now handled dynamically using component_key
// No need for hardcoded constants

// Generic validation configuration for pipeline components
// Maps component keys to their required field validators
interface ComponentValidationRule {
  fieldName: string;
  getValue: () => string | undefined;
  errorMessage: string;
}

type ComponentValidationConfig = {
  [componentKey: string]: ComponentValidationRule[];
};

const PipelineComponentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const toast = useToast();
  const projectId = useSelector(selectSelectedPilotId);

  // Helper function for consistent error handling
  const handleApiError = (error: any, context: string) => {
    console.error(`${context} error:`, error);
    const errorMessage = error?.message || error?.details || 'An unexpected error occurred';
    toast.error(`${context}: ${errorMessage}`);
  };

  // Validation state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  const [sections, setSections] = useState<PipelineSection[]>([]);
  const [selectedFile, setSelectedFile] = useState<{ [key: string]: File | null }>({});
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingComponents, setIsLoadingComponents] = useState(true);
  
  // RECO configuration state
  const [selectedLocale, setSelectedLocale] = useState<string>('');
  const [selectedSourcePilotId, setSelectedSourcePilotId] = useState<string>('');
  const [availableLocales, setAvailableLocales] = useState<BglOption<string>[]>([]); // Locales from selected pilot's supportedLocales
  const [pilots, setPilots] = useState<BglOption<string>[]>([]);
  const [pilotsData, setPilotsData] = useState<any[]>([]); // Store full pilot data for locale filtering
  const [isLoadingRecoData, setIsLoadingRecoData] = useState(false);

  // Rate Comparison configuration state
  const [ratePlansComparisonMap, setRatePlansComparisonMap] = useState<string>('');

  // Fetch pipeline components data
  useEffect(() => {
    const fetchPipelineComponents = async () => {
      if (!projectId) {
        console.warn('No projectId available for fetching pipeline components');
        setIsLoadingComponents(false);
        return;
      }

      setIsLoadingComponents(true);
      
      try {
        const response = await PipelineMasterApis.getPipelineComponents(rc, projectId);
        
        if (response.error) {
          handleApiError(response.error, 'Failed to fetch pipeline components');
          return;
        }

        if (response.data) {
          console.log('Pipeline Components:', response.data);
          setSections(response?.data?.data);
        }
      } catch (err) {
        handleApiError(err, 'Failed to fetch pipeline components');
      } finally {
        setIsLoadingComponents(false);
      }
    };

    fetchPipelineComponents();
  }, [rc, projectId]);

  // Fetch RECO-related data (pilots)
  useEffect(() => {
    const fetchRecoData = async () => {
      setIsLoadingRecoData(true);
      
      try {
        const pilotsResp = await PilotApis.getStrapiProjects(rc);

        if (pilotsResp.error) {
          console.error('Failed to fetch pilots:', pilotsResp.error);
        } else if (pilotsResp.data) {
          const pilotsDataArray = (pilotsResp.data as any)?.data || [];
          setPilotsData(pilotsDataArray);
          const pilotOptions: BglOption<string>[] = pilotsDataArray.map((pilot: any) => ({
            value: pilot.pilotId || String(pilot.id),
            label: `${pilot.pilotId} - ${pilot.accountType || 'N/A'}`
          }));
          setPilots(pilotOptions);
        }
      } catch (err) {
        console.error('Error fetching RECO data:', err);
      } finally {
        setIsLoadingRecoData(false);
      }
    };

    fetchRecoData();
  }, [rc]);


  const handleSectionToggle = (sectionId: string) => {
    setSections(prev => prev.map(section => 
      section.id === sectionId 
        ? { ...section, expanded: !section.expanded }
        : section
    ));
  };

  const handleSectionExpansion = (sectionId: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleComponentToggle = (sectionId: string, componentId: string) => {
    setSections(prev => prev.map(section => 
      section.id === sectionId 
        ? {
            ...section,
            components: section.components.map(component =>
              component.component_id === componentId
                ? { ...component, enabled: !component.enabled }
                : component
            )
          }
        : section
    ));
  };

  // Handle source pilot selection and update available locales
  const handleSourcePilotChange = (pilotId: string) => {
    setSelectedSourcePilotId(pilotId);
    setSelectedLocale(''); // Reset locale selection when pilot changes
    
    // Clear validation error for source pilot when changed
    if (validationErrors.sourcePilotId) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated.sourcePilotId;
        return updated;
      });
    }
    
    if (pilotId && pilotsData.length > 0) {
      const selectedPilot = pilotsData.find((pilot: any) => pilot.pilotId === pilotId);
      if (selectedPilot && selectedPilot.supportedLocales && Array.isArray(selectedPilot.supportedLocales)) {
        // Create locale options directly from the pilot's supportedLocales array
        const localeOptions: BglOption<string>[] = selectedPilot.supportedLocales.map((localeCode: string) => ({
          value: localeCode,
          label: localeCode // Display the locale code directly (e.g., "en", "it-IT", "ar-AE")
        }));
        setAvailableLocales(localeOptions);
      } else {
        setAvailableLocales([]);
      }
    } else {
      setAvailableLocales([]);
    }
  };

  // Handle locale change and clear validation error
  const handleLocaleChange = (locale: string) => {
    setSelectedLocale(locale);
    if (validationErrors.locale) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated.locale;
        return updated;
      });
    }
  };

  // Handle rate plans comparison map change and validate JSON
  const handleRatePlansComparisonMapChange = (value: string) => {
    setRatePlansComparisonMap(value);
    
    // Clear validation error when user starts typing
    if (validationErrors.ratePlansComparisonMap) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated.ratePlansComparisonMap;
        return updated;
      });
    }
  };

  // Helper function to check if component is Rate Comparison
  const isRateComparisonComponent = (component: any): boolean => {
    const componentKey = getComponentKey(component);
    return componentKey === COMPONENT_KEYS.RATE_COMPARISON;
  };

  // Validate JSON format
  const validateJsonFormat = (jsonString: string): { isValid: boolean; error?: string } => {
    if (!jsonString || jsonString.trim() === '') {
      return { isValid: false, error: 'Rate plans comparison map is required' };
    }
    
    try {
      const parsed = JSON.parse(jsonString);
      
      // Validate it's an object
      if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
        return { isValid: false, error: 'Rate plans comparison map must be a valid JSON object' };
      }
      
      // Validate structure: keys should be strings, values should be arrays
      for (const [key, value] of Object.entries(parsed)) {
        if (typeof key !== 'string') {
          return { isValid: false, error: 'All keys in the map must be strings' };
        }
        if (!Array.isArray(value)) {
          return { isValid: false, error: 'All values in the map must be arrays' };
        }
        // Validate array elements are numbers or strings
        for (const item of value) {
          if (typeof item !== 'number' && typeof item !== 'string') {
            return { isValid: false, error: 'Array elements must be numbers or strings' };
          }
        }
      }
      
      return { isValid: true };
    } catch (error) {
      return { isValid: false, error: 'Invalid JSON format. Please check your syntax' };
    }
  };

  // Generic validation function for pipeline components
  const validatePipelineComponents = (): { isValid: boolean; errors: Record<string, string> } => {
    const errors: Record<string, string> = {};
    
    // Get all enabled components
    const enabledComponents = sections
      .flatMap(section => section.components)
      .filter(component => component.enabled);
    
    // Group components by their component_key
    const componentsByKey = new Map<string, any[]>();
    
    enabledComponents.forEach(component => {
      const componentKey = getComponentKey(component);
      if (!componentsByKey.has(componentKey)) {
        componentsByKey.set(componentKey, []);
      }
      componentsByKey.get(componentKey)!.push(component);
    });
    
    // Define validation rules for each component key
    const validationConfig: ComponentValidationConfig = {
      [COMPONENT_KEYS.RECO_AND_INSIGHTS]: [
        {
          fieldName: 'sourcePilotId',
          getValue: () => selectedSourcePilotId,
          errorMessage: 'Source Pilot ID is required for RECO configuration'
        },
        {
          fieldName: 'locale',
          getValue: () => selectedLocale,
          errorMessage: 'Locale is required for RECO configuration'
        }
      ]
      // Future components can be added here:
      // [COMPONENT_KEYS.NBI]: [
      //   {
      //     fieldName: 'someField',
      //     getValue: () => someStateValue,
      //     errorMessage: 'Some field is required for NBI configuration'
      //   }
      // ]
    };
    
    // Validate Rate Comparison component
    const rateComparisonComponents = enabledComponents.filter(comp => isRateComparisonComponent(comp));
    if (rateComparisonComponents.length > 0) {
      const jsonValidation = validateJsonFormat(ratePlansComparisonMap);
      if (!jsonValidation.isValid) {
        errors.ratePlansComparisonMap = jsonValidation.error || 'Rate plans comparison map is invalid';
      }
    }
    
    // Validate each enabled component
    for (const [componentKey, componentsList] of Array.from(componentsByKey.entries())) {
      const validationRules = validationConfig[componentKey];
      
      if (validationRules && validationRules.length > 0) {
        validationRules.forEach(rule => {
          const value = rule.getValue();
          if (!value || value.trim() === '') {
            errors[rule.fieldName] = rule.errorMessage;
          }
        });
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  const handleFileUpload = (componentId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(prev => ({ ...prev, [componentId]: file }));
      // Update the component's file upload status
      setSections(prev => prev.map(section => ({
        ...section,
        components: section.components.map(component =>
          component.component_id === componentId
            ? {
                ...component,
                fileUpload: { 
                  ...component.fileUpload, 
                  fileName: file.name, 
                  status: 'uploaded' as const 
                }
              }
            : component
        )
      })));
    }
  };

  const handleSave = async () => {
    // Validate projectId before proceeding
    if (!projectId) {
      toast.error('No project selected. Please select a project first.');
      return;
    }

    // Validate pipeline components
    const validation = validatePipelineComponents();
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      // Show first error message
      const firstError = Object.values(validation.errors)[0];
      toast.error(firstError);
      return;
    }

    // Clear validation errors if validation passes
    setValidationErrors({});

    setIsSaving(true);
    
    try {
      // Build the components configuration for the unified API using component keys
      const components: PipelineProcessComponents = {};

      // Get all enabled components
      const enabledComponents = sections
        .flatMap(section => section.components)
        .filter(component => component.enabled);

      // Group components by their component_key
      const componentsByKey = new Map<string, any[]>();
      
      enabledComponents.forEach(component => {
        const componentKey = getComponentKey(component);
        
        if (!componentsByKey.has(componentKey)) {
          componentsByKey.set(componentKey, []);
        }
        componentsByKey.get(componentKey)!.push(component);
      });

      // Process each component key group
      for (const [componentKey, componentsList] of Array.from(componentsByKey.entries())) {
        console.log(`Processing component key: ${componentKey} with ${componentsList.length} components`);
        
        // Special handling for hybrid models to aggregate fuel types
        if (componentKey === COMPONENT_KEYS.HYBRID) {
          const hybridModelMap = new Map([
            ['Hybrid 1.0 Model (Gas)', { version: 'v1', fuelType: 'GAS' }],
            ['Hybrid 1.0 Model (Water)', { version: 'v1', fuelType: 'WATER' }],
            ['Hybrid 1.0 Model (ELECTRICITY)', { version: 'v1', fuelType: 'ELECTRICITY' }],
            ['Hybrid 2.0 Model (ELECTRICITY)', { version: 'v2', fuelType: 'ELECTRICITY' }]
          ]);
          
          const hybridModels = componentsList
            .filter((component: any) => hybridModelMap.has(component.name))
            .map((component: any) => ({
          ...component,
          ...hybridModelMap.get(component.name)!
        }));
      
          if (hybridModels.length > 0) {
            const v1Fuels = hybridModels
              .filter((model: any) => model.version === 'v1')
              .map((model: any) => model.fuelType);

            const v2Fuels = hybridModels
              .filter((model: any) => model.version === 'v2')
              .map((model: any) => model.fuelType);

            // Get master_id from the first component in the list
            const masterId = componentsList[0]?.id;
            
            components[componentKey] = {
              master_id: masterId,
              v1_fuels: v1Fuels,
              v2_fuels: v2Fuels
            };
          } else {
            // No hybrid models found, use minimal payload with master_id
            const masterId = componentsList[0]?.id;
            const minimalPayload = createMinimalPayload(componentKey);
            components[componentKey] = {
              ...minimalPayload,
              master_id: masterId
            };
          }
        } else if (componentKey === COMPONENT_KEYS.RECO_AND_INSIGHTS) {
          // Special handling for RECO - include source_pilot_id and locale if provided
          const recoPayload: any = {};
          
          // Get master_id from the first component in the list
          const masterId = componentsList[0]?.id;
          recoPayload.master_id = masterId;
          
          if (selectedSourcePilotId) {
            recoPayload.source_pilot_id = selectedSourcePilotId;
          }
          
          if (selectedLocale) {
            recoPayload.locale = selectedLocale;
          }
          
          // If no manual selection, backend will use auto-detection
          components[componentKey] = Object.keys(recoPayload).length > 1 ? recoPayload : { ...createMinimalPayload(componentKey), master_id: masterId };
        } else if (componentsList.some(comp => isRateComparisonComponent(comp))) {
          // Special handling for Rate Comparison - include rate_plans_comparison_map if provided
          const rateComparisonPayload: any = {};
          
          // Get master_id from the first component in the list
          const masterId = componentsList[0]?.id;
          rateComparisonPayload.master_id = masterId;
          
          if (ratePlansComparisonMap && ratePlansComparisonMap.trim() !== '') {
            try {
              const parsedMap = JSON.parse(ratePlansComparisonMap);
              rateComparisonPayload.rate_plans_comparison_map = parsedMap;
            } catch (error) {
              // This should not happen as validation should catch it, but handle gracefully
              console.error('Error parsing rate plans comparison map:', error);
              rateComparisonPayload.rate_plans_comparison_map = {};
            }
          }
          
          // Use the component key from the first matching component
          const rateComparisonComponent = componentsList.find(comp => isRateComparisonComponent(comp));
          const rateComparisonKey = rateComparisonComponent ? getComponentKey(rateComparisonComponent) : componentKey;
          components[rateComparisonKey] = Object.keys(rateComparisonPayload).length > 1 
            ? rateComparisonPayload 
            : { ...createMinimalPayload(rateComparisonKey), master_id: masterId };
        } else {
          // For all other components, use minimal payload with master_id
          const masterId = componentsList[0]?.id;
          const minimalPayload = createMinimalPayload(componentKey);
          components[componentKey] = {
            ...minimalPayload,
            master_id: masterId
          };
        }
      }

      // Call the unified pipeline process API
      console.log('Calling unified pipeline process API with:', { projectId, components });
      
      const response = await PipelineMasterApis.processPipeline(rc, projectId, components);
        
        if (response.error) {
        handleApiError(response.error, 'Failed to process pipeline components');
        return;
        }
        
        // Log the response for debugging
      console.log('Pipeline process API response:', response);
      
      // Parse and display component-specific messages
      // Contract returns: { data: apiData, error: null }
      // apiData is the axios response.data which has structure: { data: { components: {...}, ... }, message, success }
      const apiResponse = response.data as any;
      const responseData = apiResponse?.data || apiResponse;
      
      if (responseData && responseData.components) {
        const componentResults = responseData.components;
        const componentNames = Object.keys(componentResults);
        
        // Show individual toast for each component based on its status
        // Priority: error_text > process_success > configs_pushed
        componentNames.forEach((componentKey: string) => {
          const componentResult = componentResults[componentKey];
          
          // Priority 1: Check for error_text (highest priority) - show error
          if (componentResult.error_text) {
            toast.error(`${componentKey}: ${componentResult.error_text}`);
          }
          // Priority 2: Check if process failed - show error
          else if (componentResult.process_success === false) {
            toast.error(`${componentKey}: Process failed`);
          }
          // Priority 3: Check if configs were not pushed - show warning
          else if (componentResult.configs_pushed === false) {
            toast.warning(`${componentKey}: Configs not pushed`);
          }
          // Success case - both process_success and configs_pushed are true
          else if (componentResult.process_success === true && componentResult.configs_pushed === true) {
            toast.success(`${componentKey}: Processed successfully`);
          }
          // Fallback: if process_success is true but configs_pushed is false/undefined
          else if (componentResult.process_success === true) {
            toast.success(`${componentKey}: Processed successfully`);
          }
        });
      } else {
        // Fallback for unexpected response format
          toast.success('Pipeline components configuration saved successfully!');
      }
      
    } catch (error) {
      handleApiError(error, 'Failed to save pipeline components configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
    }}>
      <Box sx={{ 
        p: 3,
      }}>
        <Box sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h4" sx={{ 
              fontSize: '1.8rem', 
              display: 'flex', 
              alignItems: 'center'
            }}>
              <Workflow style={{ marginRight: 16, color: `${theme.palette.primary.main}` }} size={34} />
              Edit Project Setup
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
          <Card sx={{ bgcolor: 'background.paper', boxShadow: 4, borderRadius: 2 }}>
            {/* Card Header */}
            <Box sx={{ pl: 3, pt: 2, pb: 0 }}>
              <Typography variant="h4" sx={{ 
                fontSize: '1.6rem',
                display: 'flex', 
                alignItems: 'center',
                mb: 1
              }}>
                <Settings style={{ marginRight: 16, color: `${theme.palette.primary.main}` }} size={30} />
                Pipeline Components
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1rem', color: 'text.secondary', textAlign: "left" }}>
                Enable and configure the required pipeline components for this project.
              </Typography>
            </Box>
            <CardContent sx={{ p: 4, pt: 2 }}>
              {/* Sections */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {sections.some(section => section.components.length > 0) ? (
                  sections.map((section) => {
                    const isExpanded = expandedSections.includes(section.id);
                    
                    return (
                      <Box
                        key={section.id}
                        sx={{
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.secondary.dark}`,
                          overflow: 'hidden',
                        }}
                      >
                <Accordion 
                  expanded={isExpanded}
                  onChange={() => handleSectionExpansion(section.id)}
                  sx={{
                    boxShadow: 'none',
                    bgcolor: theme.palette.secondary.main,
                    '&:before': { display: 'none' },
                    '& .MuiAccordionSummary-root': {
                      bgcolor: 'transparent',
                      borderRadius: 0,
                      minHeight: 'auto',
                      '&.Mui-expanded': {
                        minHeight: 'auto',
                      }
                    },
                    '& .MuiAccordionDetails-root': {
                      p: 0,
                      bgcolor: 'background.paper',
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      px: 3, 
                      py: 2,
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        margin: 0,
                        '&.Mui-expanded': {
                          margin: 0,
                        }
                      },
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        color: theme.palette.text.secondary,
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ color: theme.palette.primary.main }}>
                        {getSectionIcon(section.icon, theme)}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16, color: 'text.primary' }}>
                        {section.name}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  
                  <AccordionDetails style={{padding:16,backgroundColor: theme.palette.secondary.main, border: "0px"}}>
                    <Box sx={{borderRadius: 2, border:"0px" }}>
                      {section.components.map((component, index) => (
                        <Box key={component.component_id} sx={{bgcolor:theme.palette.secondary.main,borderRadius: 2,mb:1 }}>
                          <ComponentItem sx={{borderRadius: 2,border: `1px solid ${theme.palette.secondary.dark}` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                              {component.name}
                            </Typography>
                            <Tooltip title={`Component: ${component.name}`}>
                              <IconButton size="small">
                                <Info size={16} color={theme.palette.text.secondary} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Switch
                            checked={component.enabled}
                            onChange={() => handleComponentToggle(section.id, component.component_id)}
                            size="small"
                            sx={{
                              width: 38,
                              height: 22,
                              padding: 0,
                              '& .MuiSwitch-switchBase': {
                                padding: 0,
                                margin: '2px',
                                transitionDuration: '300ms',
                                '&.Mui-checked': {
                                  transform: 'translateX(16px)',
                                  color: '#fff',
                                  '& + .MuiSwitch-track': {
                                    backgroundColor: theme.palette.primary.main,
                                    opacity: 1,
                                    border: 0,
                                  },
                                  '& .MuiSwitch-thumb': {
                                    color: '#fff',
                                  },
                                },
                                '&.Mui-focusVisible .MuiSwitch-thumb': {
                                  color: theme.palette.primary.main,
                                  border: '6px solid #fff',
                                },
                              },
                              '& .MuiSwitch-thumb': {
                                boxSizing: 'border-box',
                                width: 18,
                                height: 18,
                                color: '#fff',
                              },
                              '& .MuiSwitch-track': {
                                borderRadius: 22 / 2,
                                backgroundColor: '#E9E9EA',
                                opacity: 1,
                                transition: theme.transitions.create(['background-color'], {
                                  duration: 500,
                                }),
                              },
                            }}
                          />
                        </ComponentItem>

                        {/* RECO Configuration Section */}
                        {component.enabled && getComponentKey(component) === COMPONENT_KEYS.RECO_AND_INSIGHTS && (
                          <>
                            {/* Source Pilot ID Dropdown */}
                            <FileUploadSection>
                              <Box sx={{
                                bgcolor: theme.palette.secondary.dark, 
                                p: 2, 
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.secondary.dark}`,
                                width: '100%'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                    Source Pilot ID to copy the RECOs
                                  </Typography>
                                  <Tooltip title="Select the source pilot ID from which to copy RECOs">
                                    <IconButton size="small" sx={{ padding: 0.5 }}>
                                      <Info size={16} color={theme.palette.text.secondary} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Box sx={{ width: '100%' }}>
                                  <BglSelect
                                    value={selectedSourcePilotId}
                                    onChange={(value) => handleSourcePilotChange(value)}
                                    options={pilots}
                                    placeholder="Select an option"
                                    disabled={isLoadingRecoData}
                                    loading={isLoadingRecoData}
                                    sx={{ 
                                      width: '100%', 
                                      '& .MuiSelect-select': { textAlign: 'left', fontFamily: 'inherit', py: 1 },
                                      '& .MuiFormControl-root': { width: '100%' },
                                      ...(validationErrors.sourcePilotId ? {
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderColor: theme.palette.error.main
                                        }
                                      } : {})
                                    }}
                                  />
                                  {validationErrors.sourcePilotId && (
                                    <Typography variant="caption" sx={{ color: theme.palette.error.main, mt: 0.5, display: 'block' }}>
                                      {validationErrors.sourcePilotId}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </FileUploadSection>
                            
                            {/* Supported Locales Dropdown */}
                            <FileUploadSection>
                              <Box sx={{
                                bgcolor: theme.palette.secondary.dark, 
                                p: 2, 
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.secondary.dark}`,
                                width: '100%'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                    Supported Locales
                                  </Typography>
                                  <Tooltip title="Select the locale for RECO configuration">
                                    <IconButton size="small" sx={{ padding: 0.5 }}>
                                      <Info size={16} color={theme.palette.text.secondary} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Box sx={{ width: '100%' }}>
                                  <BglSelect
                                    value={selectedLocale}
                                    onChange={(value) => handleLocaleChange(value)}
                                    options={availableLocales}
                                    placeholder={selectedSourcePilotId ? "Select an option" : "Select a pilot first"}
                                    disabled={isLoadingRecoData || !selectedSourcePilotId || availableLocales.length === 0}
                                    loading={isLoadingRecoData}
                                    sx={{ 
                                      width: '100%', 
                                      '& .MuiSelect-select': { textAlign: 'left', fontFamily: 'inherit', py: 1 },
                                      '& .MuiFormControl-root': { width: '100%' },
                                      ...(validationErrors.locale ? {
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderColor: theme.palette.error.main
                                        }
                                      } : {})
                                    }}
                                  />
                                  {validationErrors.locale && (
                                    <Typography variant="caption" sx={{ color: theme.palette.error.main, mt: 0.5, display: 'block' }}>
                                      {validationErrors.locale}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </FileUploadSection>

                            
                          </>
                        )}

                        {/* Rate Comparison Configuration Section */}
                        {component.enabled && isRateComparisonComponent(component) && (
                          <FileUploadSection>
                            <Box sx={{
                              bgcolor: theme.palette.secondary.dark, 
                              p: 2, 
                              borderRadius: 2,
                              border: `1px solid ${theme.palette.secondary.dark}`,
                              width: '100%'
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                  Rate plans comparison map
                                </Typography>
                                <Tooltip title="Enter a JSON object mapping rate plan IDs to arrays of comparable rate plan IDs. Example: {'1':[2,3,4,5],'2':[3,4,5],'37':[34,42,59]}">
                                  <IconButton size="small" sx={{ padding: 0.5 }}>
                                    <Info size={16} color={theme.palette.text.secondary} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Box sx={{ width: '100%' }}>
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={4}
                                  maxRows={10}
                                  value={ratePlansComparisonMap}
                                  onChange={(e) => handleRatePlansComparisonMapChange(e.target.value)}
                                  placeholder='e.g., {"1":[2,3,4,5],"2":[3,4,5],"37":[34,42,59]}'
                                  error={!!validationErrors.ratePlansComparisonMap}
                                  sx={{
                                    width: '100%',
                                    '& .MuiOutlinedInput-root': {
                                      bgcolor: theme.palette.background.paper,
                                      color: theme.palette.text.primary,
                                      fontFamily: 'monospace',
                                      fontSize: '0.875rem',
                                      '& fieldset': {
                                        borderColor: validationErrors.ratePlansComparisonMap 
                                          ? theme.palette.error.main 
                                          : theme.palette.secondary.dark,
                                      },
                                      '&:hover fieldset': {
                                        borderColor: validationErrors.ratePlansComparisonMap 
                                          ? theme.palette.error.main 
                                          : theme.palette.primary.main,
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: validationErrors.ratePlansComparisonMap 
                                          ? theme.palette.error.main 
                                          : theme.palette.primary.main,
                                      },
                                    },
                                    '& .MuiInputBase-input': {
                                      color: theme.palette.text.primary,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                      color: theme.palette.text.secondary,
                                      opacity: 0.6,
                                    },
                                  }}
                                />
                                {validationErrors.ratePlansComparisonMap && (
                                  <Typography variant="caption" sx={{ color: theme.palette.error.main, mt: 0.5, display: 'block' }}>
                                    {validationErrors.ratePlansComparisonMap}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </FileUploadSection>
                        )}

                        {/* File Upload Section for enabled components
                        {component.enabled && getComponentKey(component) !== COMPONENT_KEYS.RECO_AND_INSIGHTS && component.fileUpload && (
                          <FileUploadSection>
                            <Box sx={{bgcolor: theme.palette.secondary.dark, p:2, borderRadius: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                    {component.fileText}
                                </Typography>
                                <Tooltip title={`Upload the config  uration file for ${component.name}`}>
                                  <IconButton size="small">
                                    <Info size={16} color={theme.palette.text.secondary} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Box sx={{ marginTop: 1, display: 'flex', justifyContent: 'flex-start', gap: 2,width: '100%',mt:1 }}>
                                <input
                                  accept=".json,.csv,.xlsx"
                                  style={{ display: 'none' }}
                                  id={`file-upload-${component.component_id}`}
                                  type="file"
                                  onChange={(e) => handleFileUpload(component.component_id, e)}
                                />
                                <label htmlFor={`file-upload-${component.component_id}`} style={{width: '100%'}}>
                                  <StyledButton
                                    variant="outlined"
                                    sx={{
                                      borderColor: theme.palette.secondary.dark,
                                      backgroundColor:theme.palette.secondary.light,
                                      color: theme.palette.text.primary,
                                      
                                      "&:hover": {
                                        backgroundColor: theme.palette.secondary.light,
                                        borderColor: theme.palette.secondary.dark,
                                      },
                                      justifyContent:"flex-start",
                                      width: "100%",
                                      cursor: 'pointer',
                                    }}
                                  >
                                    Choose File
                                    <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: selectedFile[component.component_id] ? theme.palette.success.main : theme.palette.text.secondary, 
                                    marginLeft: 2, 
                                    display: 'inline',
                                    fontWeight: selectedFile[component.component_id] ? 500 : 400
                                  }}
                                >
                                  {selectedFile[component.component_id]?.name || 'No file chosen'}
                                </Typography>
                                  </StyledButton>
                                  
                                </label>
                                
                                {selectedFile[component.component_id] && (
                                  <Typography variant="caption" sx={{ color: theme.palette.success.main, marginLeft: 1 }}>
                                    ✓ Uploaded
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </FileUploadSection>
                        )} */}
                      </Box>
                    ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            );
          })
                ) : !projectId ? (
                  <Box sx={{ 
                    padding: 4, 
                    textAlign: 'center',
                    color: theme.palette.text.secondary
                  }}>
                    <Typography variant="h6" sx={{ marginBottom: 1, color: theme.palette.text.primary }}>
                      No project selected
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Please select a project to view pipeline components.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ 
                    padding: 4, 
                    textAlign: 'center',
                    color: theme.palette.text.secondary
                  }}>
                    <Typography variant="h6" sx={{ marginBottom: 1, color: theme.palette.text.primary }}>
                      No components found
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Try adjusting your filter criteria to see more components.
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Footer Buttons */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mt: 4,
                pt: 3,
                borderTop: `1px solid ${theme.palette.secondary.dark}`,
              }}>
                <StyledButton
                  variant="outlined"
                  startIcon={<ArrowLeft size={16} />}
                  onClick={handleBack}
                  sx={{
                    borderColor: theme.palette.secondary.dark,
                    color: theme.palette.text.primary,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: theme.palette.secondary.light
                    }
                  }}
                >
                  Back to Project Details
                </StyledButton>
                <StyledButton
                  variant="contained"
                  startIcon={<Save size={16} />}
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark
                    },
                    '&:disabled': {
                      backgroundColor: theme.palette.grey[400],
                      color: theme.palette.grey[600]
                    }
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </StyledButton>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
      
      
      {/* Overlay Loader */}
      <BglOverlayLoader
        open={isSaving || isLoadingComponents}
        message={
          isLoadingComponents
            ? "Loading pipeline components..."
            : isSaving 
              ? "Saving configuration and processing components..." 
              : "Processing..."
        }
        size={50}
      />
      
    </Box>
  );
};

export default PipelineComponentsPage;